import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiDelete, apiDownload, ApiError } from '../api/client';
import { businessBySlug } from '../domain/businesses';
import { formatMkd, formatDate, todayIso, monthAgoIso } from '../lib/format';
import CategoryTiles from '../components/CategoryTiles';
import type { Category } from '../components/CategoryTiles';
import FilterBar, { btn } from '../components/FilterBar';
import PlannedModal from '../components/PlannedModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/**
 * Shpenzimet e Planifikuara for one business.
 *
 * Replaces frmShpenzimetPlanifikuara{Stargym,Starbar,Missfit,Stargroup} plus
 * their grid and dialog partners — twelve files differing only by a hardcoded
 * biznesiID.
 */

interface PlannedRow {
  ID: number;
  data: string;
  artikulli: string | null;
  cmimi: number;
  periudha: string | null;
}

export default function ShpenzimetPlanifikuara() {
  const { biznesi = '' } = useParams();
  const business = businessBySlug(biznesi);
  const queryClient = useQueryClient();
  const toast = useToast();

  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(monthAgoIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [deleting, setDeleting] = useState<PlannedRow | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ['planned-categories', biznesi],
    queryFn: () =>
      apiGet<{ categories: Category[] }>(`/shpenzimet/planifikuara/${biznesi}/kategorite`),
    enabled: Boolean(business),
  });

  const categories = categoriesQuery.data?.categories ?? [];

  // Reset when the business changes so a category id never leaks across.
  useEffect(() => {
    setCategoryId(null);
    setSearch('');
  }, [biznesi]);

  useEffect(() => {
    if (categoryId === null && categories.length > 0) setCategoryId(categories[0]!.id);
  }, [categories, categoryId]);

  const rowsQuery = useQuery({
    queryKey: ['planned-rows', biznesi, categoryId, search, startDate, endDate],
    queryFn: () =>
      apiGet<{ rows: PlannedRow[]; total: number }>(
        `/shpenzimet/planifikuara/${biznesi}/shpenzimet`,
        { kategoriaId: categoryId, search, startDate, endDate },
      ),
    enabled: Boolean(business) && categoryId !== null,
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/shpenzimet/planifikuara/shpenzimi/${id}`),
    onSuccess: () => {
      toast.success('Rreshti u fshi me sukses.');
      refreshAll();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Fshirja dështoi.'),
  });

  function refreshAll() {
    void queryClient.invalidateQueries({ queryKey: ['planned-rows'] });
    void queryClient.invalidateQueries({ queryKey: ['planned-categories'] });
  }

  async function handleExport() {
    try {
      await apiDownload(`/shpenzimet/planifikuara/${biznesi}/eksport`, {
        kategoriaId: categoryId,
        search,
        startDate,
        endDate,
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.');
    }
  }

  if (!business) {
    return <div className="rounded-xl bg-white p-6 text-red-700">Biznesi nuk u gjet.</div>;
  }

  const rows = rowsQuery.data?.rows ?? [];
  const total = rowsQuery.data?.total ?? 0;
  const activeCategory = categories.find((c) => c.id === categoryId);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">
          Shpenzimet e planifikuara — {business.name}
        </h1>
        <p className="text-sm text-muted">Totalet e kategorive për muajin aktual</p>
      </div>

      <CategoryTiles
        categories={categories}
        loading={categoriesQuery.isLoading}
        selectedId={categoryId}
        onSelect={setCategoryId}
      />

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        searchLabel="Kërko artikull"
      >
        <button
          type="button"
          onClick={handleExport}
          disabled={rows.length === 0}
          className={btn.secondary}
        >
          Eksporto Excel
        </button>
        <button
          type="button"
          onClick={() => setEditing({ id: null })}
          disabled={categoryId === null}
          className={btn.primary}
        >
          Planifiko shpenzim
        </button>
      </FilterBar>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Artikulli</th>
                <th className="px-4 py-3 font-semibold">Periudha</th>
                <th className="px-4 py-3 text-right font-semibold">Çmimi</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {rowsQuery.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}

              {!rowsQuery.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted">
                    Nuk ka të dhëna për këtë periudhë.
                  </td>
                </tr>
              )}

              {rows.map((row, index) => (
                <tr
                  key={row.ID}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                >
                  <td className="px-4 py-2.5 text-muted">{index + 1}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(row.data)}</td>
                  <td className="px-4 py-2.5">{row.artikulli ?? 'N/A'}</td>
                  <td className="px-4 py-2.5 text-muted">{row.periudha ?? ''}</td>
                  <td className="tnum px-4 py-2.5 text-right font-medium">
                    {formatMkd(row.cmimi)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing({ id: row.ID })}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        Ndrysho
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(row)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        Fshij
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <span className="text-sm text-muted">
            Shuma totale nga {formatDate(startDate)} deri më {formatDate(endDate)}:
          </span>
          <span className="tnum text-lg font-semibold text-ink">{formatMkd(total)}</span>
        </div>
      </div>

      {editing && categoryId !== null && (
        <PlannedModal
          biznesi={biznesi}
          businessName={business.name}
          kategoriaId={categoryId}
          categoryName={activeCategory?.kategoria ?? ''}
          plannedId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null);
            toast.success(message);
            refreshAll();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo fshirjen"
          message="A jeni i sigurt që dëshironi të fshini këtë rresht?"
          confirmLabel="Fshij"
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleting.ID);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
